import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useToast } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { clientFetch } from "@flights/client-fetch";
import { getSearch } from "../../../store/search/selectors.js";
import { getSearchResultsPriceAlertStatus, getSearchResultsCachedBestFare } from "../../../store/searchResults/selectors.js";
import flights_lve_web_price_alerts, { useTrackPriceAlertsWeb, trackSubscriptionCreatedWithBookingWindow } from "../../../utils/experiments/lve/flights_lve_web_price_alerts.js";
import useProfiler from "../../../hooks/useProfiler.js";
export const useIsPriceAlertsEligible = () => {
    const { isLoggedInAuthLow } = useProfiler();
    const priceAlertStatus = useSelector(getSearchResultsPriceAlertStatus);
    const trackPriceAlertsWeb = useTrackPriceAlertsWeb();
    return (!!(priceAlertStatus?.isEligible && priceAlertStatus?.isSearchEligible) &&
        isLoggedInAuthLow() &&
        trackPriceAlertsWeb());
};
const SUBSCRIPTIONS_URL = "/api/priceAlerts/subscriptions";
export function usePriceAlertToggle() {
    const i18n = useI18n();
    const search = useSelector(getSearch);
    const priceAlertStatus = useSelector(getSearchResultsPriceAlertStatus);
    const bestFare = useSelector(getSearchResultsCachedBestFare);
    const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [subscriptionId, setSubscriptionId] = useState(undefined);
    const { show: showToast } = useToast();
    const { isDesktop } = useProfiler();
    const trackPriceAlertsWeb = useTrackPriceAlertsWeb();
    const searchCriteria = useMemo(() => {
        const segment = search.searchSegments[0];
        const from = segment?.from[0];
        const to = segment?.to[0];
        if (!from || !to || !segment?.departureDate)
            return undefined;
        if (!from.code || !from.type || !to.code || !to.type)
            return undefined;
        return {
            origin: `${from.code}.${from.type}`,
            originName: from.name,
            destination: `${to.code}.${to.type}`,
            destinationName: to.name,
            departDate: segment.departureDate,
            returnDate: search.type === "ROUNDTRIP" ? segment.returnDate : undefined,
            cabinClass: search.cabinClass,
            type: search.type,
            adults: search.adults,
            children: search.children.length > 0 ? search.children : undefined
        };
    }, [search]);
    useEffect(() => {
        setIsSubscriptionActive(priceAlertStatus?.priceAlert !== undefined);
        const id = priceAlertStatus?.priceAlert?.subscriptionId;
        setSubscriptionId(id !== undefined ? Number(id) : undefined);
    }, [priceAlertStatus]);
    const isRecentlyCreated = isSubscriptionActive && priceAlertStatus?.priceAlert?.newlyCreated === true;
    const suggestion = priceAlertStatus?.priceAlert?.suggestion;
    const suggestionUrl = useMemo(() => {
        if (!suggestion || !searchCriteria)
            return undefined;
        const params = new URLSearchParams();
        params.set("type", searchCriteria.type);
        params.set("adults", String(searchCriteria.adults));
        params.set("cabinClass", searchCriteria.cabinClass);
        params.set("depart", suggestion.departDate);
        params.set("from", searchCriteria.origin);
        params.set("to", searchCriteria.destination);
        if (suggestion.returnDate)
            params.set("return", suggestion.returnDate);
        searchCriteria.children?.forEach((age) => params.append("children", String(age)));
        return `/flights/${searchCriteria.origin}-${searchCriteria.destination}/?${params.toString()}`;
    }, [suggestion, searchCriteria]);
    const onToggle = useCallback((checked) => {
        if (isLoading)
            return;
        if (checked && !searchCriteria)
            return;
        if (!checked && !subscriptionId)
            return;
        setIsLoading(true);
        const request = checked
            ? clientFetch(SUBSCRIPTIONS_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ searchCriteria, bestFare })
            })
                .then(({ subscriptionId: id }) => {
                setSubscriptionId(id);
                setIsSubscriptionActive(true);
                if (searchCriteria) {
                    trackPriceAlertsWeb();
                    trackSubscriptionCreatedWithBookingWindow(searchCriteria.departDate);
                }
                showToast({
                    text: i18n.trans(t("flights_price_alerts_web_sr_toggle_on_toast_body")),
                    action: {
                        text: i18n.trans(t("flights_price_alerts_web_sr_toggle_toast_link")),
                        href: "https://www.booking.com/mywishlist.html?wl_tab=price_alerts",
                        onClick: () => flights_lve_web_price_alerts.goals.manageAlertsToastClick()
                    },
                    ...(isDesktop() && {
                        attributes: { style: { insetInlineStart: "auto", insetInlineEnd: "var(--bui_spacing_6x)" } }
                    })
                });
            })
                .catch(() => {
                setIsSubscriptionActive(false);
            })
            : clientFetch(SUBSCRIPTIONS_URL, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ subscriptionId })
            })
                .then(() => {
                setIsSubscriptionActive(false);
                showToast({
                    text: i18n.trans(t("flights_price_alerts_web_sr_toggle_off_toast_body")),
                    action: {
                        text: i18n.trans(t("flights_price_alerts_web_sr_toggle_toast_link")),
                        href: "https://www.booking.com/mywishlist.html?wl_tab=price_alerts",
                        onClick: () => flights_lve_web_price_alerts.goals.manageAlertsToastClick()
                    },
                    ...(isDesktop() && {
                        attributes: { style: { insetInlineStart: "auto", insetInlineEnd: "var(--bui_spacing_6x)" } }
                    })
                });
            })
                .catch(() => {
                setIsSubscriptionActive(true);
            });
        void request.finally(() => setIsLoading(false));
    }, [isLoading, searchCriteria, bestFare, subscriptionId, showToast, i18n, isDesktop, trackPriceAlertsWeb]);
    const result = {
        isSubscriptionActive: isSubscriptionActive,
        isLoading,
        isRecentlyCreated,
        priceTrend: priceAlertStatus?.priceAlert?.priceTrend,
        percentageChange: priceAlertStatus?.priceAlert?.percentageChange,
        suggestion,
        suggestionUrl,
        lastNotified: priceAlertStatus?.priceAlert?.lastNotified,
        onToggle
    };
    return result;
}
